import {
  BarChart, Bar, Cell, ReferenceLine, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import type { UptimeBucket, UptimeGraphData } from "../../types/status";

const UPTIME_COLOR = "#1D345F";
const DOWNTIME_COLOR = "#dc2626";

interface UptimeTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: UptimeBucket }>;
  label?: string;
}

function UptimeTooltipContent({ active, payload, label }: UptimeTooltipProps) {
  if (!active || !payload?.length) return null;
  const bucket = payload[0].payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs shadow-md max-w-[220px]">
      <p className="mb-1.5 font-semibold text-gray-700">{label}</p>
      <p className="text-gray-600">Uptime: <span className="font-medium">{bucket.uptime_pct.toFixed(2)}%</span></p>
      {bucket.downtime_minutes > 0 && (
        <>
          <p className="mt-1 text-red-600">Downtime: {bucket.downtime_minutes}min</p>
          {bucket.incidents.map((inc) => (
            <div key={inc.id} className="mt-1.5 border-t border-gray-100 pt-1.5">
              <p className="font-medium text-gray-700 truncate">{inc.service_name}</p>
              <p className="text-gray-400">
                {inc.duration_minutes != null ? `${inc.duration_minutes}min` : "ongoing"}
                {inc.error_message ? ` · ${inc.error_message.slice(0, 40)}` : ""}
              </p>
            </div>
          ))}
          <p className="mt-2 font-medium text-blue-500">Click to view incidents →</p>
        </>
      )}
    </div>
  );
}

export default function UptimeGraph({
  graphData,
  loading,
  onDowntimePeriodClick,
  sourceLabel,
}: {
  graphData: UptimeGraphData | null;
  loading: boolean;
  onDowntimePeriodClick: (ids: string[]) => void;
  sourceLabel: string;
}) {
  if (loading) {
    return (
      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <div className="h-3 w-28 rounded bg-gray-100 animate-pulse" />
          <div className="h-3 w-44 rounded bg-gray-100 animate-pulse" />
        </div>
        <div className="h-[220px] w-full rounded-xl border border-gray-100 bg-gray-50 animate-pulse" />
      </div>
    );
  }

  if (!graphData?.buckets.length) return null;

  const { buckets, slo_target } = graphData;

  function handleClick(e: { activePayload?: Array<{ payload: UptimeBucket }> }) {
    const bucket = e?.activePayload?.[0]?.payload;
    if (bucket?.incidents.length) {
      onDowntimePeriodClick(bucket.incidents.map((i) => i.id));
    }
  }

  const hasAnyDowntime = buckets.some((b) => b.downtime_minutes > 0);

  return (
    <div className="mb-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Uptime &amp; downtime — {sourceLabel}</h2>
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: UPTIME_COLOR }} />
            Uptime
          </span>
          {hasAnyDowntime && (
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: DOWNTIME_COLOR }} />
              Downtime (click to drill in)
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-5 border-t-2 border-dashed border-blue-400" />
            Target SLO {slo_target}%
          </span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={buckets}
          onClick={handleClick}
          style={{ cursor: hasAnyDowntime ? "pointer" : "default" }}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          barCategoryGap="20%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={(v: number) => `${v}%`}
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            tickLine={false}
            axisLine={false}
            width={42}
          />
          <Tooltip content={<UptimeTooltipContent />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
          <ReferenceLine
            y={slo_target}
            stroke="#3b82f6"
            strokeDasharray="4 2"
            strokeWidth={1.5}
            label={{ value: `SLO ${slo_target}%`, position: "insideTopLeft", fontSize: 10, fill: "#3b82f6", dy: -4 }}
          />
          <Bar dataKey="uptime_pct" radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {buckets.map((bucket) => (
              <Cell
                key={bucket.date_iso}
                fill={bucket.downtime_minutes > 0 ? DOWNTIME_COLOR : UPTIME_COLOR}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {!hasAnyDowntime && (
        <p className="mt-2 text-center text-xs text-gray-400">
          100% uptime — no {sourceLabel === "all sources" ? "" : `${sourceLabel} `}downtime in this period.
        </p>
      )}
    </div>
  );
}
